<script lang="ts">
import type { InputProps } from '#layers/ui/components/Input.vue';
import type { FlattenedItem, NestedSelectMenuItem, NestedSelectMenuItemBase, NestedSelectMenuTheme } from './types';

export interface NestedSelectMenuLevelProps<
	TItem extends NestedSelectMenuItemBase = NestedSelectMenuItem,
> {
	items: TItem[];
	anchorElement: HTMLElement;
	level: number;
	pathSoFar: string[];
	maxNestingDepth?: number;
	searchInput?: boolean | InputProps;
	searchPlaceholder?: string;
	ui: NestedSelectMenuTheme;
	isRtl: boolean;
	portal?: boolean;
}

export interface NestedSelectMenuLevelEmits<
	TItem extends NestedSelectMenuItemBase = NestedSelectMenuItem,
> {
	select: [item: TItem, path: string[]];
	collapse: [];
	contentElement: [element: HTMLElement | null];
}
</script>

<script setup lang="ts" generic="TItem extends NestedSelectMenuItemBase = NestedSelectMenuItem">
import type { ComponentPublicInstance, ShallowRef } from 'vue';

import { useFuse } from '@vueuse/integrations/useFuse';
import {
	ComboboxAnchor,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxGroup,
	ComboboxInput,
	ComboboxItem,
	ComboboxPortal,
	ComboboxRoot,
	ComboboxViewport,
} from 'reka-ui';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, useTemplateRef, watch } from 'vue';
import { useSubmenuSafePath } from '#layers/ui/composables/use-submenu-safe-path';
import { focusFirst } from '#layers/ui/utils/autofocus';
import { injectNestedSelectMenuContext } from './NestedSelectMenuContext';
import NestedSelectMenuItemContent from './NestedSelectMenuItemContent.vue';
import { useNestedSelectMenuKeyboard } from './use-nested-select-menu-keyboard';
import { getChildren, hasChildren, preventOutsideDismissWithinMenu, setExpandedItemRef, stopInputBlurDismissWithinMenu } from './utils';

const {
	maxNestingDepth = 5,
	portal = true,
	items,
	isRtl,
	level,
	pathSoFar,
} = defineProps<NestedSelectMenuLevelProps<TItem>>();

const emit = defineEmits<NestedSelectMenuLevelEmits<TItem>>();

const slots = defineSlots<{
	'item'?: (props: { item: TItem; index: number }) => any;
	'item-leading'?: (props: { item: TItem; index: number }) => any;
	'item-label'?: (props: { item: TItem; index: number }) => any;
	'item-trailing'?: (props: { item: TItem; index: number }) => any;
}>();

const { closeAll, isTargetWithinMenu, menuId } = injectNestedSelectMenuContext();

const searchTerm = ref('');
const expandedItemId = ref<string | null>(null);

const expandedItem = computed<TItem | null>(() => items.find((item) => item.id === expandedItemId.value && hasChildren(item)) ?? null);

const expandedItemRef = ref<HTMLElement | null>(null);
const highlightedItem: ShallowRef<TItem | FlattenedItem<TItem> | null> = shallowRef(null);
const submenuContentElement = ref<HTMLElement | null>(null);

const inputRef = useTemplateRef<HTMLInputElement>('input');
const contentRef = useTemplateRef<ComponentPublicInstance | HTMLElement>('content');
const nestedMenuRef = useTemplateRef<{
	focus: () => void;
	getContentElement: () => HTMLElement | null;
}>('nestedMenu');

const { handleKeydown, expandKey } = useNestedSelectMenuKeyboard<TItem>({
	isRtl: () => isRtl,
	highlightedItem,
	expandedItemId,
	nestedMenuRef,
	onEscape: closeAll,
	onCollapse: () => emit('collapse'),
	onSelectHighlighted: handleHighlightedSelect,
});

const submenuSafePath = useSubmenuSafePath({
	submenuElement: () => submenuContentElement.value ?? nestedMenuRef.value?.getContentElement(),
	side: () => isRtl ? 'left' : 'right',
});

const { results: fuzzyResults } = useFuse(searchTerm, () => items, {
	fuseOptions: {
		keys: ['label'],
		threshold: 0.3,
	},
	resultLimit: 50,
	matchAllWhenSearchEmpty: true,
});

const filteredItems = computed<TItem[]>(() =>
	fuzzyResults.value.map((result) => result.item),
);

const expandedItemChildren = computed<TItem[]>(() =>
	expandedItem.value ? getChildren(expandedItem.value) : [],
);

watch(contentRef, async () => {
	await nextTick();
	emit('contentElement', getContentElement());
}, { flush: 'post' });

watch(expandedItem, (item) => {
	submenuContentElement.value = null;

	if (!item) {
		expandedItemId.value = null;
		expandedItemRef.value = null;
		submenuSafePath.resetSafePath();
	}
});

watch(searchTerm, (term) => {
	if (term) {
		expandedItemId.value = null;
		expandedItemRef.value = null;
	}
});

function handleHighlight(item: TItem) {
	highlightedItem.value = item;
}

function handleItemMouseEnter(item: TItem, event: MouseEvent) {
	if (item.disabled) return;

	if (
		expandedItem.value?.id !== item.id
		&& submenuSafePath.isWithinSafePath(event)
	) {
		return;
	}

	expandedItemId.value = hasChildren(item) ? item.id : null;
}

function handleHighlightedSelect(item: TItem | FlattenedItem<TItem>) {
	handleSelect(isFlattenedItem(item) ? item.item : item);
}

function isFlattenedItem(item: TItem | FlattenedItem<TItem>): item is FlattenedItem<TItem> {
	return 'item' in item && 'pathIds' in item;
}

function handleSelect(item: TItem) {
	const atMaxDepth = level >= maxNestingDepth;

	if (hasChildren(item) && !atMaxDepth) {
		if (expandedItem.value?.id !== item.id) {
			expandedItemId.value = item.id;
			return;
		}

		if (!item.selectable) {
			return;
		}
	}

	emit('select', item, [...pathSoFar, item.id]);
}

function handleInputKeydown(event: KeyboardEvent) {
	const input = event.target as HTMLInputElement;
	const atEnd = input.selectionStart === input.value.length;

	const highlightedMenuItem = highlightedItem.value && isFlattenedItem(highlightedItem.value)
		? highlightedItem.value.item
		: highlightedItem.value;

	if (event.key === expandKey.value && atEnd && highlightedMenuItem && hasChildren(highlightedMenuItem)) {
		event.preventDefault();
		expandedItemId.value = highlightedMenuItem.id;
		void focusNestedMenuAfterExpansion();
	}
}

async function focusNestedMenuAfterExpansion() {
	await nextTick();
	nestedMenuRef.value?.focus();
}

function handleChildCollapse() {
	expandedItemId.value = null;
	submenuSafePath.resetSafePath();
	focusFirst(inputRef);
}

function handleChildSelect(item: TItem, path: string[]) {
	emit('select', item, path);
}

function handleSubmenuContentElement(element: HTMLElement | null) {
	submenuContentElement.value = element;
}

function handleNestedMenuOutsideEvent(event: Event) {
	preventOutsideDismissWithinMenu(event, isTargetWithinMenu);
}

function handleNestedMenuInputBlur(event: FocusEvent) {
	stopInputBlurDismissWithinMenu(event, isTargetWithinMenu);
}

function handleExpandedItemRef(el: Element | ComponentPublicInstance | null, item: TItem) {
	setExpandedItemRef<TItem>(expandedItem, expandedItemRef, el, item);
}

onMounted(async () => {
	await nextTick();
	emit('contentElement', getContentElement());
});

onBeforeUnmount(() => {
	emit('contentElement', null);
});

defineExpose({
	focus: () => {
		focusFirst(getContentElement());
	},
	getContentElement,
});

function getContentElement(): HTMLElement | null {
	const value = contentRef.value;

	if (!value) return null;
	if (value instanceof HTMLElement) return value;
	if ('$el' in value && value.$el?.nodeType === Node.ELEMENT_NODE) return value.$el as HTMLElement;

	return null;
}
</script>

<template>
	<ComboboxRoot
		open
		ignore-filter
		as-child
		@highlight="(context) => context?.value && handleHighlight(context.value as TItem)"
	>
		<ComboboxAnchor :reference="anchorElement" as-child />

		<ComboboxPortal :disabled="!portal">
			<ComboboxContent
				position="popper"
				:side="isRtl ? 'left' : 'right'"
				:side-offset="0"
				align="start"
				:data-nested-select-menu-id="menuId"
				:class="ui.content()"
				@focus-outside="handleNestedMenuOutsideEvent"
				@interact-outside="handleNestedMenuOutsideEvent"
				@keydown="handleKeydown"
				@mouseenter="focusFirst(inputRef)"
				@mousemove="submenuSafePath.handlePointerMove"
				@pointer-down-outside="handleNestedMenuOutsideEvent"
			>
				<DAnimatedSheet>
					<div ref="content" class="flex min-w-full flex-col items-stretch" data-nested-menu-content>
						<ComboboxInput v-if="searchInput" as-child>
							<DInput
								ref="input"
								v-model="searchTerm"
								:placeholder="searchPlaceholder"
								variant="none"
								class="sticky top-0 z-10 bg-default"
								:class="[ui.input()]"
								@blur.capture="handleNestedMenuInputBlur"
								@keydown="handleInputKeydown"
							/>
						</ComboboxInput>

						<ComboboxViewport :class="ui.viewport()">
							<ComboboxEmpty :class="ui.empty()">
								{{ $t('common.no_results') }}
							</ComboboxEmpty>

							<ComboboxGroup v-if="filteredItems.length > 0" :class="ui.group()">
								<ComboboxItem
									v-for="(item, index) in filteredItems"
									:key="item.id"
									:ref="(el) => handleExpandedItemRef(el, item)"
									:value="item"
									:data-nested-item="item.id"
									:data-nested-path="JSON.stringify([...pathSoFar, item.id])"
									:disabled="item.disabled"
									:class="[
										ui.item({ class: item.ui?.item }),
										expandedItem?.id === item.id && 'text-highlighted before:bg-elevated',
									]"
									@mouseenter="handleItemMouseEnter(item, $event)"
									@select.prevent="handleSelect(item)"
								>
									<NestedSelectMenuItemContent :item :index :ui :is-rtl>
										<template v-for="(_, name) in slots" #[name]="slotData" :key="name">
											<slot :name v-bind="slotData" />
										</template>
									</NestedSelectMenuItemContent>
								</ComboboxItem>
							</ComboboxGroup>
						</ComboboxViewport>
					</div>
				</DAnimatedSheet>
			</ComboboxContent>
		</ComboboxPortal>
	</ComboboxRoot>

	<NestedSelectMenuLevel
		v-if="expandedItem && expandedItemRef && level < maxNestingDepth"
		ref="nestedMenu"
		:items="expandedItemChildren"
		:anchor-element="expandedItemRef"
		:level="level + 1"
		:path-so-far="[...pathSoFar, expandedItem.id]"
		:max-nesting-depth
		:search-input
		:search-placeholder
		:ui
		:is-rtl
		:portal
		@select="handleChildSelect"
		@collapse="handleChildCollapse"
		@content-element="handleSubmenuContentElement"
	>
		<template v-for="(_, name) in slots" #[name]="slotData" :key="name">
			<slot :name v-bind="slotData" />
		</template>
	</NestedSelectMenuLevel>
</template>
